import React, { useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Autocomplete
} from '@mui/material';

/**
 * Customer Details Component for Debit Notes
 * 
 * @param {Object} props
 * @param {Object} props.debitNote - Debit note data with customer information
 * @param {Object} props.controlStyle - Style object for form controls
 * @param {Function} props.onCustomerChange - Function to handle customer data changes
 */
const CustomerDetails = ({
  debitNote,
  controlStyle,
  onCustomerChange
}) => {
  // Log when debitNote changes to track data flow
  useEffect(() => {
    console.log('CustomerDetails: debitNote changed', debitNote);
    if (debitNote && debitNote.customer) {
      console.log('CustomerDetails: customer data received', debitNote.customer);
    }
  }, [debitNote]);
  
  // Create a safe customer object to prevent null reference errors
  const customer = debitNote.customer || {};
  
  // Check if we have any customer data to display
  const hasCustomerData = customer && (
    customer.name || 
    customer.vatNumber || 
    customer.phone || 
    customer.address
  );
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0.5, 
        mb: 0.5, 
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, fontSize: '0.875rem' }}>
        Customer Information
      </Typography>
      <Grid container spacing={0.5}>
        {hasCustomerData ? (
          <>
            <Grid item xs={12}>
              <TextField
                label="Customer Name"
                value={customer.name || ''}
                InputProps={{
                  readOnly: true,
                }}
                fullWidth
                margin="dense"
                size="small"
                required
                sx={controlStyle}
              />
            </Grid>
            <Grid item xs={12}>
              {customer.vatNumber ? (
                <TextField
                  label="VAT Number"
                  value={customer.vatNumber || ''}
                  fullWidth
                  margin="dense"
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={controlStyle}
                />
              ) : (
                <TextField
                  label="Phone Number"
                  value={customer.phone || ''}
                  fullWidth
                  margin="dense"
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={controlStyle}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                value={customer.address || customer.streetName || ''}
                fullWidth
                multiline
                rows={2.5}
                margin="dense"
                size="small"
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  ...controlStyle,
                  '& .MuiOutlinedInput-root': {
                    minHeight: 90,
                    borderRadius: 0
                  }
                }}
              />
            </Grid>
            {customer.phone && !customer.vatNumber && (
              <Grid item xs={12}>
                <TextField
                  label="Phone Number"
                  value={customer.phone || ''}
                  fullWidth
                  margin="dense"
                  size="small"
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={controlStyle}
                />
              </Grid>
            )}
          </>
        ) : (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Select an invoice to display customer information
            </Typography>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default CustomerDetails;
